"use client";

import { LESSON_DOCK_QUERY } from "@/components/whiteboard/LessonBoardDock";
import {
  type TutorAvatarDefaultAssignment,
  type TutorAvatarDefaultPostHogClient,
  exposeTutorAvatarDefaultView,
  resolveTutorAvatarDefaultView,
} from "@/lib/experiments/tutorAvatarDefaultView";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  DEFAULT_LATEST_ONLY,
  DEFAULT_TUTOR_VIEW,
  type ShownTutorView,
  type TutorView,
  readStoredLatestOnly,
  readStoredShownView,
  readStoredTutorView,
  storeLatestOnly,
  storeTutorView,
} from "./tutor-view";

/**
 * The desktop tutor views start where the lesson board docks: 1024px. Null until
 * measured on the client.
 */
export function useTutorStageViewport() {
  // Known on the first client render, so the layout does not start narrow and
  // then switch to the desktop views.
  const [wide, setWide] = useState<boolean | null>(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(LESSON_DOCK_QUERY).matches
      : null
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      setWide(false);
      return;
    }
    const query = window.matchMedia(LESSON_DOCK_QUERY);
    const update = () => setWide(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);
  return wide;
}

/**
 * Which component owns the avatar is decided once per session, from the width
 * when it starts: replacing the avatar's container disconnects the viewer, so a
 * resize or a tablet rotation must never move it to another owner.
 */
export function useSessionAvatarOwner(wide: boolean | null, sessionActive: boolean) {
  const [latched, setLatched] = useState<boolean | null>(null);
  useEffect(() => {
    if (!sessionActive) {
      setLatched(null);
      return;
    }
    if (wide === null) return;
    setLatched((current) => current ?? wide);
  }, [sessionActive, wide]);
  return sessionActive ? (latched ?? wide ?? false) : Boolean(wide);
}

/**
 * The learner's tutor view (remembered), the conversation mode beside her, and
 * the temporary fullscreen. Leaving fullscreen returns focus to the control
 * that opened it; the layout underneath never unmounts.
 */
export function useTutorStage({
  enabled,
  avatarAvailable,
  posthog,
}: {
  /** Desktop, standard transcript, avatar allowed. */
  enabled: boolean;
  /** False when the avatar video failed: views fall back to the voice-only layout. */
  avatarAvailable: boolean;
  posthog?: TutorAvatarDefaultPostHogClient | null;
}) {
  // The learner's choices are read on the first client render, so a stored
  // view never shows the default layout first. The session view only renders
  // on the client (after the room connects), so this cannot mismatch a server
  // render.
  const onClient = typeof window !== "undefined";
  const [initialPreferences] = useState(() => ({
    view: onClient ? readStoredTutorView() : null,
    shownView: onClient ? readStoredShownView() : null,
  }));
  const [selectedView, setView] = useState<TutorView | null>(initialPreferences.view);
  const defaultAssignmentRef = useRef<TutorAvatarDefaultAssignment | null>(null);
  // The transcript starts after connection; flags are normally cached by then.
  // Resolve only when the desktop stage is eligible, and never change it later.
  if (enabled && selectedView === null && !defaultAssignmentRef.current) {
    defaultAssignmentRef.current = resolveTutorAvatarDefaultView(posthog);
  }
  const defaultAssignment = defaultAssignmentRef.current;
  const defaultView = defaultAssignment?.view ?? DEFAULT_TUTOR_VIEW;
  const view = selectedView ?? defaultView;
  const [lastShownView, setShownView] = useState<ShownTutorView | null>(
    initialPreferences.shownView ??
      (initialPreferences.view !== "none" ? initialPreferences.view : null)
  );
  const shownView = lastShownView ?? (defaultView === "none" ? "compact" : defaultView);
  const exposedRef = useRef(false);
  useEffect(() => {
    if (
      exposedRef.current ||
      !enabled ||
      !avatarAvailable ||
      !posthog ||
      initialPreferences.view !== null ||
      selectedView !== null ||
      defaultAssignment?.source !== "posthog" ||
      view !== defaultAssignment.view
    )
      return;
    exposedRef.current = exposeTutorAvatarDefaultView(posthog, defaultAssignment.view);
  }, [
    avatarAvailable,
    defaultAssignment,
    enabled,
    initialPreferences.view,
    posthog,
    selectedView,
    view,
  ]);
  const [latestOnly, setLatestOnlyState] = useState(
    () => (onClient ? readStoredLatestOnly() : null) ?? DEFAULT_LATEST_ONLY
  );
  const [fullscreen, setFullscreen] = useState(false);
  const returnFocusRef = useRef<HTMLElement | null>(null);

  const setLatestOnly = useCallback((next: boolean) => {
    setLatestOnlyState(next);
    storeLatestOnly(next);
  }, []);

  const changeView = useCallback((next: TutorView) => {
    setView(next);
    if (next !== "none") setShownView(next);
    storeTutorView(next);
  }, []);

  const toggleCamera = useCallback(() => {
    changeView(view === "none" ? shownView : "none");
  }, [changeView, shownView, view]);

  const fullscreenActive = enabled && avatarAvailable && fullscreen;

  const enterFullscreen = useCallback((trigger: HTMLElement | null) => {
    returnFocusRef.current = trigger;
    setFullscreen(true);
  }, []);

  const exitFullscreen = useCallback(() => setFullscreen(false), []);

  // Focus returns once the opener is interactive again (the layout underneath
  // is inert while fullscreen is open).
  useEffect(() => {
    if (fullscreenActive) return;
    const target = returnFocusRef.current;
    if (!target) return;
    returnFocusRef.current = null;
    const frame = window.requestAnimationFrame(() => target.focus());
    return () => window.cancelAnimationFrame(frame);
  }, [fullscreenActive]);

  // Escape leaves fullscreen and nothing else: the page shell and the transcript
  // treat Escape as "end the session", so this listener runs first (window,
  // capture phase) and stops the event there.
  useEffect(() => {
    if (!fullscreenActive) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setFullscreen(false);
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [fullscreenActive]);

  // Leaving the desktop layout or losing the video ends fullscreen.
  useEffect(() => {
    if (!enabled || !avatarAvailable) setFullscreen(false);
  }, [avatarAvailable, enabled]);

  const conversationView = enabled && (view === "compact" || view === "large");

  return {
    view,
    shownView,
    changeView,
    toggleCamera,
    cameraOn: view !== "none",
    latestOnly,
    setLatestOnly,
    fullscreenActive,
    enterFullscreen,
    exitFullscreen,
    conversationView,
    /** The avatar is drawn: hidden ("Off") pauses rendering unless fullscreen shows her. */
    renderingHidden: view === "none" && !fullscreenActive,
  };
}

export type TutorStageState = ReturnType<typeof useTutorStage>;
